import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { API_BASE_URL } from '../config';
import { useCustomers } from '../context/CustomerContext';
import { displayName } from '../utils/customerDisplay';
import Alert from './Alert';
import Button from './Button';
import CustomerSelect from './CustomerSelect';
import { DeviceIdPicker } from './DeviceIdPicker';
import './InventoryPage.css';
import LoadingSpinner from './LoadingSpinner';

const ORDER_PAGE_SIZE = 50;

interface ByCustomerRow {
  customer_tag: string;
  sku_code: string;
  product_name: string | null;
  active: number;
  broken: number;
  lost: number;
}

interface InventoryItem {
  sku_code: string;
  product_name: string;
  new_inventory: number;
  returns: number;
  active: number;
  broken: number;
  lost: number;
  available: number;
  /** Units on a return shipment that has not arrived yet. See inventory_math.py. */
  pending_return: number;
  last_updated: string;
}

interface OrderItem {
  id: number;
  product_name: string;
  sku_code: string;
  quantity: number;
  transaction_type: 'IN' | 'OUT' | 'INV' | 'BROKEN' | 'LOST';
  order_date: string;
  reference_number: string;
  created_at: string;
  customer_tag?: string;
  status_reason?: string | null;
  device_ids?: string[];
}

interface SKUItem {
  sku_code: string;
  product_name: string;
  created_at: string;
  new_inventory?: number;
  returns?: number;
  active?: number;
  broken?: number;
  lost?: number;
  available?: number;
  pending_return?: number;
}

interface PackageItem {
  id?: number;
  sku_code: string;
  product_name?: string | null;
  quantity: number;
  display_order?: number;
}

interface ProductPackage {
  id: number;
  name: string;
  description: string | null;
  is_active: number;
  created_at: string;
  updated_at: string;
  items: PackageItem[];
}

interface PackageLine {
  sku_code: string;
  product_name: string;
  quantity: number;
}

interface NewOrder {
  product_name: string;
  sku_code: string;
  quantity: number;
  transaction_type: 'IN' | 'OUT' | 'INV' | 'BROKEN' | 'LOST';
  order_date: string;
  reference_number: string;
  customer_tag?: string;
  device_ids?: string[];
  status_reason?: string;
}

const formatDateTime = (dateStr: string) => {
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
  }).format(date);
};

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
  }).format(date);
};

const InventoryPage: React.FC = () => {
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [orders, setOrders] = useState<OrderItem[]>([]);
  const [skus, setSkus] = useState<SKUItem[]>([]);
  const [error, setError] = useState<{ type: 'error' | 'warning', message: string } | null>(null);
  const [newProductName, setNewProductName] = useState('');
  const [showNewOrderForm, setShowNewOrderForm] = useState(false);
  const { customers } = useCustomers();
  const [newOrder, setNewOrder] = useState<NewOrder>(() => {
    // Generate default reference number for INV transaction type
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0]; // YYYY-MM-DD
    const defaultReference = `order-${dateStr}`;
    
    return {
      product_name: '',
      sku_code: '',
      quantity: 0,
      transaction_type: 'INV',
      order_date: today.toISOString().split('T')[0],
      reference_number: defaultReference,
      customer_tag: undefined
    };
  });
  const [statusReasons, setStatusReasons] = useState<{id: number; reason: string}[]>([]);
  const [addingReason, setAddingReason] = useState(false);
  const [newReason, setNewReason] = useState('');
  const [productFilter, setProductFilter] = useState<string>(''); // sku_code, '' = all
  const [customerFilter, setCustomerFilter] = useState<string>('');
  const [orderTotal, setOrderTotal] = useState<number>(0);
  const [hasMoreOrders, setHasMoreOrders] = useState<boolean>(false);
  const [loadingMoreOrders, setLoadingMoreOrders] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'by-sku' | 'by-customer' | 'packages'>('by-sku');
  const [byCustomerData, setByCustomerData] = useState<ByCustomerRow[] | null>(null);
  const [byCustomerLoading, setByCustomerLoading] = useState<boolean>(false);
  const [packages, setPackages] = useState<ProductPackage[] | null>(null);
  const [packagesLoading, setPackagesLoading] = useState<boolean>(false);
  const [editingPackage, setEditingPackage] = useState<ProductPackage | null>(null);
  const [packageDraft, setPackageDraft] = useState<{ name: string; description: string; items: PackageItem[] }>({
    name: '', description: '', items: [],
  });
  const [packageSaving, setPackageSaving] = useState<boolean>(false);
  // "Adjust by package": a picked package expands into editable order lines
  // that all share the form's Type / Date / Reference / Customer, mirroring
  // applyPackage() in the shipping-label flow.
  const [adjustPackageId, setAdjustPackageId] = useState<string>('');
  const [adjustMultiplier, setAdjustMultiplier] = useState<number>(1);
  const [packageLines, setPackageLines] = useState<PackageLine[]>([]);
  const [packageError, setPackageError] = useState<string | null>(null);
  const [editingSkuCode, setEditingSkuCode] = useState<string | null>(null);
  const [editingSkuName, setEditingSkuName] = useState<string>('');
  const [editingOrderId, setEditingOrderId] = useState<number | null>(null);
  const [editingOrder, setEditingOrder] = useState<{
    quantity: number;
    transaction_type: 'IN' | 'OUT' | 'INV' | 'BROKEN' | 'LOST';
    customer_tag?: string;
  }>({
    quantity: 0,
    transaction_type: 'INV',
    customer_tag: undefined
  });

  const fetchInventory = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/inventory`);
      if (!response.ok) throw new Error('Failed to fetch inventory');
      const data = await response.json();
      setInventory(data);
    } catch (err) {
      setError({ type: 'error', message: 'Failed to load inventory' });
      console.error(err);
    }
  };

  const fetchOrders = useCallback(async (
    opts: { offset?: number; append?: boolean } = {}
  ) => {
    const offset = opts.offset ?? 0;
    const append = opts.append ?? false;
    try {
      if (append) setLoadingMoreOrders(true);
      const params = new URLSearchParams({
        limit: String(ORDER_PAGE_SIZE),
        offset: String(offset),
      });
      if (productFilter) params.set('sku_code', productFilter);
      if (customerFilter) params.set('customer_tag', customerFilter);
      const response = await fetch(`${API_BASE_URL}/api/orders?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to fetch orders');
      const data = await response.json();
      setOrders(prev => append ? [...prev, ...data.orders] : data.orders);
      setOrderTotal(data.total);
      setHasMoreOrders(data.hasMore);
    } catch (err) {
      setError({ type: 'error', message: 'Failed to load orders' });
      console.error(err);
    } finally {
      if (append) setLoadingMoreOrders(false);
    }
  }, [productFilter, customerFilter]);

  const fetchByCustomer = useCallback(async () => {
    try {
      setByCustomerLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/inventory/by-customer`);
      if (!response.ok) throw new Error('Failed to fetch by-customer inventory');
      const data: ByCustomerRow[] = await response.json();
      setByCustomerData(data);
    } catch (err) {
      setError({ type: 'error', message: 'Failed to load customer inventory' });
      console.error(err);
    } finally {
      setByCustomerLoading(false);
    }
  }, []);

  const fetchSKUs = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/skus`);
      if (!response.ok) throw new Error('Failed to fetch SKUs');
      const data = await response.json();
      setSkus(data);
    } catch (err) {
      setError({ type: 'error', message: 'Failed to load SKUs' });
      console.error(err);
    }
  };

  const fetchPackages = useCallback(async () => {
    try {
      setPackagesLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/packages`);
      if (!response.ok) throw new Error('Failed to fetch packages');
      const data: ProductPackage[] = await response.json();
      setPackages(data);
    } catch (err) {
      setError({ type: 'error', message: 'Failed to load packages' });
      console.error(err);
    } finally {
      setPackagesLoading(false);
    }
  }, []);

  const openCreatePackage = () => {
    setEditingPackage(null);
    setPackageDraft({ name: '', description: '', items: [{ sku_code: '', quantity: 1 }] });
    setPackageError(null);
  };

  const openEditPackage = (pkg: ProductPackage) => {
    setEditingPackage(pkg);
    setPackageDraft({
      name: pkg.name,
      description: pkg.description || '',
      items: pkg.items.length > 0
        ? pkg.items.map(it => ({ sku_code: it.sku_code, quantity: it.quantity }))
        : [{ sku_code: '', quantity: 1 }],
    });
    setPackageError(null);
  };

  const closePackageModal = () => {
    setEditingPackage(null);
    setPackageDraft({ name: '', description: '', items: [] });
    setPackageError(null);
  };

  const updatePackageItem = (index: number, patch: Partial<PackageItem>) => {
    setPackageDraft(prev => ({
      ...prev,
      items: prev.items.map((it, i) => i === index ? { ...it, ...patch } : it),
    }));
  };

  const addPackageItem = () => {
    setPackageDraft(prev => ({
      ...prev,
      items: [...prev.items, { sku_code: '', quantity: 1 }],
    }));
  };

  const removePackageItem = (index: number) => {
    setPackageDraft(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const savePackage = async () => {
    setPackageError(null);
    const name = packageDraft.name.trim();
    if (!name) { setPackageError('Name is required'); return; }
    const items = packageDraft.items
      .filter(it => it.sku_code)
      .map(it => ({ sku_code: it.sku_code, quantity: Math.max(1, Math.floor(it.quantity || 1)) }));
    if (items.length === 0) {
      setPackageError('Add at least one item with a SKU');
      return;
    }
    try {
      setPackageSaving(true);
      const isEdit = editingPackage !== null;
      const url = isEdit
        ? `${API_BASE_URL}/api/packages/${editingPackage!.id}`
        : `${API_BASE_URL}/api/packages`;
      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, description: packageDraft.description.trim(), items }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to save package');
      }
      closePackageModal();
      fetchPackages();
    } catch (err) {
      setPackageError(err instanceof Error ? err.message : 'Failed to save package');
    } finally {
      setPackageSaving(false);
    }
  };

  const deletePackage = async (pkg: ProductPackage) => {
    if (!window.confirm(`Delete package "${pkg.name}"? Past shipments are unaffected.`)) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/packages/${pkg.id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete package');
      fetchPackages();
    } catch (err) {
      setError({ type: 'error', message: err instanceof Error ? err.message : 'Failed to delete package' });
    }
  };

  // Fetch initial inventory + skus on mount
  useEffect(() => {
    fetchInventory();
    fetchSKUs();
    // Also needed by the "Adjust by package" picker in the order form, which
    // lives outside the packages tab.
    fetchPackages();
  }, [fetchPackages]);

  // Refetch orders whenever filters change (resets to first page)
  useEffect(() => {
    fetchOrders({ offset: 0, append: false });
  }, [fetchOrders]);

  // Load the reason list whenever the transaction type is BROKEN or LOST —
  // the reason dropdown only makes sense for those two.
  useEffect(() => {
    const type = newOrder.transaction_type;
    if (type !== 'BROKEN' && type !== 'LOST') { setStatusReasons([]); return; }
    fetch(`${API_BASE_URL}/api/status-reasons?transaction_type=${type}`)
      .then(r => r.json())
      .then(setStatusReasons)
      .catch(() => setStatusReasons([]));
  }, [newOrder.transaction_type]);

  const submitNewReason = async () => {
    const reason = newReason.trim();
    if (!reason) return;
    const resp = await fetch(`${API_BASE_URL}/api/status-reasons`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ transaction_type: newOrder.transaction_type, reason }),
    });
    if (resp.ok) {
      const saved = await resp.json();
      setStatusReasons(prev => [...prev, saved]);
      setNewOrder({ ...newOrder, status_reason: saved.reason });
    }
    setNewReason('');
    setAddingReason(false);
  };

  // Lazy-load by-customer view on first toggle
  useEffect(() => {
    if (viewMode === 'by-customer' && byCustomerData === null && !byCustomerLoading) {
      fetchByCustomer();
    }
  }, [viewMode, byCustomerData, byCustomerLoading, fetchByCustomer, packages, packagesLoading, fetchPackages]);

  const invalidateByCustomer = () => setByCustomerData(null);

  // Expand the picked package into editable lines. Quantities are the
  // package's own, times the multiplier, and stay editable before submit.
  const applyPackageToOrder = () => {
    const pkg = (packages ?? []).find(p => p.id === Number(adjustPackageId));
    if (!pkg || pkg.items.length === 0) return;
    const multiplier = Math.max(1, adjustMultiplier || 1);
    // The single-SKU picker is hidden while package lines are expanded, but
    // hiding it does not clear what it selected: without this, a device chosen
    // for one camera line rode out on every line of the package.
    setNewOrder(prev => ({ ...prev, device_ids: undefined }));
    setPackageLines(pkg.items.map(item => {
      const matched = skus.find(sku => sku.sku_code === item.sku_code);
      return {
        sku_code: item.sku_code,
        product_name: matched?.product_name || item.product_name || item.sku_code,
        quantity: item.quantity * multiplier,
      };
    }));
  };

  const clearPackageLines = () => {
    setPackageLines([]);
    setAdjustPackageId('');
    setAdjustMultiplier(1);
  };

  const resetOrderForm = () => {
    const dateStr = new Date().toISOString().split('T')[0];
    setNewOrder({
      product_name: '',
      sku_code: '',
      quantity: 0,
      transaction_type: 'INV',
      order_date: dateStr,
      reference_number: `order-${dateStr}`,
      customer_tag: undefined,
      device_ids: undefined,
      status_reason: undefined
    });
    setShowNewOrderForm(false);
    setAddingReason(false);
    setNewReason('');
    clearPackageLines();
  };

  // One request for the whole kit — /api/orders/bulk commits every line or
  // none, so a package adjustment can't land half-applied.
  const submitPackageLines = async () => {
    const response = await fetch(`${API_BASE_URL}/api/orders/bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: packageLines.map(line => ({
          sku_code: line.sku_code,
          product_name: line.product_name,
          quantity: line.quantity,
          // A package line has no picker of its own, so there is nothing to
          // send. Reading newOrder.device_ids here would put the single-SKU
          // form's selection on every line of the package.
          device_ids: [],
        })),
        transaction_type: newOrder.transaction_type,
        order_date: newOrder.order_date,
        reference_number: newOrder.reference_number,
        customer_tag: newOrder.customer_tag,
        status_reason: newOrder.status_reason || null,
      }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to create package adjustment');
    }
  };

  const handleNewOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (packageLines.length > 0) {
        await submitPackageLines();
        resetOrderForm();
        invalidateByCustomer();
        await Promise.all([fetchInventory(), fetchOrders({ offset: 0, append: false })]);
        return;
      }
      // Add timestamp to reference_number to make it unique
      const timestamp = new Date().toISOString().replace(/[-:T]/g, '').substring(0, 14);
      const orderWithTimestamp = {
        ...newOrder,
        reference_number: `${newOrder.reference_number}-${timestamp}`,
        device_ids: newOrder.device_ids || [],
        status_reason: newOrder.status_reason || null
      };

      const response = await fetch(`${API_BASE_URL}/api/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderWithTimestamp),
      });

      if (!response.ok) {
        throw new Error('Failed to create order');
      }

      // Reset form
      const today = new Date();
      const dateStr = today.toISOString().split('T')[0];
      setNewOrder({
        product_name: '',
        sku_code: '',
        quantity: 0,
        transaction_type: 'INV',
        order_date: dateStr,
        reference_number: `order-${dateStr}`,
        customer_tag: undefined,
        device_ids: undefined,
        status_reason: undefined
      });
      setShowNewOrderForm(false);
      setAddingReason(false);
      setNewReason('');

      // Refresh data
      invalidateByCustomer();
      await Promise.all([fetchInventory(), fetchOrders({ offset: 0, append: false })]);
    } catch (err) {
      setError({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to create order',
      });
      console.error(err);
    }
  };

  const handleCreateSKU = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/api/skus`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ product_name: newProductName }),
      });

      if (!response.ok) {
        throw new Error('Failed to create SKU');
      }

      // Clear the input
      setNewProductName('');
      
      // Refresh SKUs and inventory
      await Promise.all([fetchSKUs(), fetchInventory()]);
    } catch (err) {
      setError({ type: 'error', message: 'Failed to create SKU' });
      console.error(err);
    }
  };

  const deleteOrder = async (id: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/orders/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete order');
      }

      // Refresh both tables after deletion
      invalidateByCustomer();
      await Promise.all([fetchInventory(), fetchOrders()]);
    } catch (err) {
      setError({ type: 'error', message: 'Failed to delete order' });
      console.error(err);
    }
  };

  const startEditingOrder = (order: OrderItem) => {
    setEditingOrderId(order.id);
    setEditingOrder({
      quantity: order.quantity,
      transaction_type: order.transaction_type,
      customer_tag: order.customer_tag
    });
  };

  const cancelEditingOrder = () => {
    setEditingOrderId(null);
  };

  const saveOrderChanges = async (id: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/orders/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          quantity: editingOrder.quantity,
          transaction_type: editingOrder.transaction_type,
          customer_tag: editingOrder.customer_tag
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update order');
      }

      // Reset editing state
      setEditingOrderId(null);
      
      // Refresh both tables after update
      invalidateByCustomer();
      await Promise.all([fetchInventory(), fetchOrders()]);
    } catch (err) {
      setError({ type: 'error', message: 'Failed to update order' });
      console.error(err);
    }
  };

  const deleteSKU = async (sku_code: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/skus/${sku_code}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete SKU');
      }

      // Refresh both tables after deletion
      await Promise.all([fetchInventory(), fetchSKUs()]);
    } catch (err) {
      setError({ type: 'error', message: 'Failed to delete SKU' });
      console.error(err);
    }
  };

  // Merge SKUs and inventory data
  const mergedInventory = useMemo(() => {
    const inventoryMap = new Map(inventory.map(item => [item.sku_code, item]));
    return skus.map(sku => ({
      ...sku,
      new_inventory: inventoryMap.get(sku.sku_code)?.new_inventory || 0,
      returns: inventoryMap.get(sku.sku_code)?.returns || 0,
      active: inventoryMap.get(sku.sku_code)?.active || 0,
      broken: inventoryMap.get(sku.sku_code)?.broken || 0,
      lost: inventoryMap.get(sku.sku_code)?.lost || 0,
      available: inventoryMap.get(sku.sku_code)?.available || 0,
      pending_return: inventoryMap.get(sku.sku_code)?.pending_return || 0,
      last_updated: inventoryMap.get(sku.sku_code)?.last_updated || sku.created_at
    }));
  }, [inventory, skus]);

  // Canonical product name lookup — orders denormalize product_name at insert
  // time, so to reflect renames we read from the live sku table on display.
  const skuMap = useMemo(
    () => new Map(skus.map(s => [s.sku_code, s.product_name])),
    [skus],
  );
  const productNameFor = (order: OrderItem) =>
    skuMap.get(order.sku_code) ?? order.product_name;

  // Customer lookup for displayName
  const customerByTag = useMemo(
    () => new Map(customers.map(c => [c.customer_tag, c])),
    [customers],
  );
  const customerLabel = (tag?: string | null) => {
    if (!tag) return 'None';
    const c = customerByTag.get(tag);
    return c ? displayName(c) : tag;
  };

  const startRenameSku = (item: { sku_code: string; product_name: string }) => {
    setEditingSkuCode(item.sku_code);
    setEditingSkuName(item.product_name);
  };

  const cancelRenameSku = () => {
    setEditingSkuCode(null);
    setEditingSkuName('');
  };

  const saveRenameSku = async (sku_code: string) => {
    const trimmed = editingSkuName.trim();
    if (!trimmed) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/skus/${sku_code}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_name: trimmed }),
      });
      if (!response.ok) throw new Error('Failed to rename product');
      setEditingSkuCode(null);
      setEditingSkuName('');
      // SKU list drives skuMap; refetch inventory so JOIN-derived names refresh too.
      invalidateByCustomer();
      await Promise.all([fetchSKUs(), fetchInventory()]);
    } catch (err) {
      setError({ type: 'error', message: 'Failed to rename product' });
      console.error(err);
    }
  };

  return (
    <div className="inventory-page">
      {error && error.message && (
        <Alert type={error.type === 'error' ? 'error' : 'warning'}>
          {error.message}
        </Alert>
      )}

      <div className="inventory-summary">
        <div className="inventory-header">
          <h3>Inventory & SKUs</h3>
          <div className="view-mode-toggle" role="tablist" aria-label="Inventory view">
            <Button
              variant={viewMode === 'by-sku' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('by-sku')}
              aria-pressed={viewMode === 'by-sku'}
            >
              By SKU
            </Button>
            <Button
              variant={viewMode === 'by-customer' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('by-customer')}
              aria-pressed={viewMode === 'by-customer'}
            >
              By Customer
            </Button>
            <Button
              variant={viewMode === 'packages' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('packages')}
              aria-pressed={viewMode === 'packages'}
            >
              Packages
            </Button>
          </div>
          {viewMode === 'packages' ? (
            <Button size="sm" onClick={openCreatePackage}>+ New Package</Button>
          ) : (
            <form onSubmit={handleCreateSKU} className="sku-form">
              <input
                type="text"
                value={newProductName}
                onChange={(e) => setNewProductName(e.target.value)}
                placeholder="Enter product name"
                required
              />
              <Button type="submit" size="sm">Add SKU</Button>
            </form>
          )}
        </div>
        {viewMode === 'by-sku' ? (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Product Name</th>
                  <th>SKU Code</th>
                  <th>Total Inventory</th>
                  <th>Active</th>
                  <th>Broken</th>
                  <th>Lost</th>
                  <th>Available</th>
                  <th title="Units already counted in Available but still in transit back to us. On-hand = Available - Pending Return.">
                    Pending Return
                  </th>
                  <th>Created At</th>
                  <th>Last Updated</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {[...mergedInventory]
                  .sort((a, b) => (a.product_name || '').localeCompare(b.product_name || ''))
                  .map((item) => (
                    <tr key={item.sku_code}>
                      <td>
                        {editingSkuCode === item.sku_code ? (
                          <div className="inline-edit">
                            <input
                              type="text"
                              value={editingSkuName}
                              onChange={(e) => setEditingSkuName(e.target.value)}
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveRenameSku(item.sku_code);
                                if (e.key === 'Escape') cancelRenameSku();
                              }}
                            />
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() => saveRenameSku(item.sku_code)}
                            >
                              Save
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={cancelRenameSku}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <span className="sku-name-cell">
                            {item.product_name}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="edit-button"
                              onClick={() => startRenameSku(item)}
                              aria-label="Rename product"
                              title="Rename product"
                            >
                              ✎
                            </Button>
                          </span>
                        )}
                      </td>
                      <td>{item.sku_code}</td>
                      <td>{item.new_inventory}</td>
                      <td>{item.active}</td>
                      <td className={item.broken > 0 ? "broken-count" : ""}>{item.broken || 0}</td>
                      <td className={item.lost > 0 ? "lost-count" : ""}>{item.lost || 0}</td>
                      <td>{item.available}</td>
                      <td
                        className={item.pending_return > 0 ? 'pending-return-count' : ''}
                        title={
                          item.pending_return > 0
                            ? `${item.pending_return} unit(s) in transit — ${item.available - item.pending_return} actually on hand`
                            : undefined
                        }
                      >
                        {item.pending_return || 0}
                      </td>
                      <td>{formatDateTime(item.created_at)}</td>
                      <td>{formatDateTime(item.last_updated)}</td>
                      <td>
                        {item.available === 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="delete-button"
                            onClick={() => deleteSKU(item.sku_code)}
                            aria-label="Delete SKU"
                          >
                            ×
                          </Button>
                        )}
                      </td>
                    </tr>
                ))}
                {mergedInventory.length === 0 && (
                  <tr><td colSpan={10} className="empty-state">No inventory items found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        ) : viewMode === 'by-customer' ? (
          <>
            <div className="by-customer-controls">
              <CustomerSelect
                className="customer-filter"
                value={customerFilter}
                onChange={setCustomerFilter}
                options={[...customers]
                  .sort((a, b) => displayName(a).localeCompare(displayName(b)))
                  .map(c => ({ value: c.customer_tag, label: displayName(c) }))}
                placeholder="Filter by customer..."
                allowEmpty
                emptyLabel="All Customers"
              />
            </div>
            <div className="table-container">
              {byCustomerLoading && byCustomerData === null ? (
                <LoadingSpinner message="Loading customer inventory..." size="sm" inline />
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Product Name</th>
                      <th>SKU Code</th>
                      <th>Active</th>
                      <th>Broken</th>
                      <th>Lost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(byCustomerData ?? [])
                      .filter(row => !customerFilter || row.customer_tag === customerFilter)
                      .map((row) => (
                        <tr key={`${row.customer_tag}|${row.sku_code}`}>
                          <td>{customerLabel(row.customer_tag)}</td>
                          <td>{skuMap.get(row.sku_code) ?? row.product_name ?? row.sku_code}</td>
                          <td>{row.sku_code}</td>
                          <td>{row.active}</td>
                          <td className={row.broken > 0 ? 'broken-count' : ''}>{row.broken}</td>
                          <td className={row.lost > 0 ? 'lost-count' : ''}>{row.lost}</td>
                        </tr>
                    ))}
                    {(byCustomerData ?? []).filter(row => !customerFilter || row.customer_tag === customerFilter).length === 0 && !byCustomerLoading && (
                      <tr><td colSpan={6} className="empty-state">No customer inventory found</td></tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </>
        ) : (
          <div className="table-container">
            {packagesLoading && packages === null ? (
              <LoadingSpinner message="Loading packages..." size="sm" inline />
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Items</th>
                    <th>Updated</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(packages ?? []).map(pkg => (
                    <tr key={pkg.id}>
                      <td>{pkg.name}</td>
                      <td>{pkg.description || <span className="empty-state">—</span>}</td>
                      <td>
                        {pkg.items.length === 0 ? '0' : (
                          <span title={pkg.items.map(i => `${i.product_name || i.sku_code} ×${i.quantity}`).join(', ')}>
                            {pkg.items.length}
                          </span>
                        )}
                      </td>
                      <td>{formatDate(pkg.updated_at)}</td>
                      <td className="package-row-actions">
                        <Button variant="ghost" size="sm" onClick={() => openEditPackage(pkg)}>Edit</Button>
                        <Button variant="ghost" size="sm" onClick={() => deletePackage(pkg)}>Delete</Button>
                      </td>
                    </tr>
                  ))}
                  {(packages ?? []).length === 0 && !packagesLoading && (
                    <tr><td colSpan={5} className="empty-state">No packages yet. Click "+ New Package" to create one.</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      <div className="orders-table">
        <div className="orders-header">
          <h3>Order History</h3>
          <div className="orders-controls">
            <select
              className="product-filter"
              value={productFilter}
              onChange={(e) => setProductFilter(e.target.value)}
            >
              <option value="">All Products</option>
              {[...skus]
                .sort((a, b) => a.product_name.localeCompare(b.product_name))
                .map(sku => (
                  <option key={sku.sku_code} value={sku.sku_code}>
                    {sku.product_name}
                  </option>
                ))
              }
            </select>

            <CustomerSelect
              className="customer-filter"
              value={customerFilter}
              onChange={setCustomerFilter}
              options={[...customers]
                .sort((a, b) => displayName(a).localeCompare(displayName(b)))
                .map(c => ({ value: c.customer_tag, label: displayName(c) }))}
              placeholder="Filter by customer..."
              allowEmpty
              emptyLabel="All Customers"
            />
            
            <Button
              onClick={() => {
                if (showNewOrderForm) {
                  // Hide the form and drop any expanded package lines
                  resetOrderForm();
                } else {
                  // Reset form with current date when opening
                  const today = new Date();
                  const dateStr = today.toISOString().split('T')[0];
                  setNewOrder({
                    product_name: '',
                    sku_code: '',
                    quantity: 0,
                    transaction_type: 'INV',
                    order_date: dateStr,
                    reference_number: `order-${dateStr}`,
                    customer_tag: undefined,
                    device_ids: undefined,
                    status_reason: undefined
                  });
                  setAddingReason(false);
                  setNewReason('');
                  setShowNewOrderForm(true);
                }
              }}
            >
              {showNewOrderForm ? 'Cancel' : 'New Order'}
            </Button>
          </div>
        </div>

        {showNewOrderForm && (
          <div className="new-order-form">
            <form onSubmit={handleNewOrder}>
              {(packages ?? []).length > 0 && (
                <div className="package-adjust-bar">
                  <span className="package-adjust-label">Adjust by package</span>
                  <select
                    id="adjust-package"
                    className="package-adjust-select"
                    aria-label="Package"
                    value={adjustPackageId}
                    onChange={(e) => setAdjustPackageId(e.target.value)}
                  >
                    <option value="">Select a package</option>
                    {(packages ?? []).map(pkg => (
                      <option key={pkg.id} value={pkg.id}>{pkg.name}</option>
                    ))}
                  </select>
                  <span className="package-adjust-times" aria-hidden="true">×</span>
                  <input
                    id="adjust-multiplier"
                    className="package-adjust-multiplier"
                    type="number"
                    min="1"
                    aria-label="Number of packages"
                    value={adjustMultiplier}
                    onChange={(e) => setAdjustMultiplier(
                      e.target.value === '' ? 1 : parseInt(e.target.value))}
                  />
                  <Button type="button" size="sm" onClick={applyPackageToOrder}
                          disabled={!adjustPackageId}>
                    Apply
                  </Button>
                  {packageLines.length > 0 && (
                    <Button type="button" size="sm" variant="ghost" onClick={clearPackageLines}>
                      Clear lines
                    </Button>
                  )}
                </div>
              )}

              {packageLines.length > 0 && (
                <div className="table-container package-lines-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Product Name</th>
                        <th>SKU Code</th>
                        <th>Quantity</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {packageLines.map((line, index) => (
                        <tr key={line.sku_code}>
                          <td>{line.product_name}</td>
                          <td>{line.sku_code}</td>
                          <td>
                            <input
                              type="number"
                              min="1"
                              style={{ width: '6rem' }}
                              value={line.quantity}
                              onChange={(e) => setPackageLines(prev => prev.map((l, i) =>
                                i === index
                                  ? { ...l, quantity: e.target.value === '' ? 1 : parseInt(e.target.value) }
                                  : l))}
                            />
                          </td>
                          <td>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="delete-button"
                              onClick={() => setPackageLines(prev => prev.filter((_, i) => i !== index))}
                              title="Remove line"
                            >
                              ×
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="form-row">
                {packageLines.length === 0 && (<>
                <div className="form-group">
                  <label htmlFor="product">Product</label>
                  <select
                    id="product"
                    value={`${newOrder.product_name}|${newOrder.sku_code}`}
                    onChange={(e) => {
                      const [product_name, sku_code] = e.target.value.split('|');
                      setNewOrder({
                        ...newOrder,
                        product_name,
                        sku_code
                      });
                    }}
                    required
                  >
                    <option value="">Select a product</option>
                    {[...skus].sort((a, b) => a.product_name.localeCompare(b.product_name)).map((sku) => (
                      <option key={sku.sku_code} value={`${sku.product_name}|${sku.sku_code}`}>
                        {sku.product_name} ({sku.sku_code})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="quantity">Quantity</label>
                  <input
                    id="quantity"
                    type="number"
                    min="1"
                    value={newOrder.quantity || ''}
                    onChange={(e) => setNewOrder({
                      ...newOrder,
                      quantity: e.target.value === '' ? 0 : parseInt(e.target.value)
                    })}
                    required
                  />
                </div>

                <DeviceIdPicker
                  customerTag={newOrder.customer_tag}
                  skuCode={newOrder.sku_code}
                  quantity={newOrder.quantity || 0}
                  mode="out"
                  value={newOrder.device_ids || []}
                  onChange={(device_ids) => setNewOrder(prev => ({ ...prev, device_ids }))}
                />
                </>)}

                <div className="form-group">
                  <label htmlFor="type">Type</label>
                  <select
                    id="type"
                    value={newOrder.transaction_type}
                    onChange={(e) => {
                      const newType = e.target.value as 'IN' | 'OUT' | 'INV' | 'BROKEN' | 'LOST';
                      // Auto-generate reference_number for inventory transactions
                      let reference = newOrder.reference_number;
                      if (newType === 'INV') {
                        // Format: order-YYYY-MM-DD
                        const today = new Date();
                        const dateStr = today.toISOString().split('T')[0]; // YYYY-MM-DD
                        reference = `order-${dateStr}`;
                      }
                      setNewOrder({
                        ...newOrder,
                        transaction_type: newType,
                        reference_number: reference,
                        // Set customer_tag to undefined for INV transactions
                        customer_tag: newType === 'INV' ? undefined : newOrder.customer_tag,
                        // A device selection and a BROKEN/LOST reason both
                        // belong to a specific transaction type — carrying
                        // either into an unrelated type would attach a
                        // stale reason or device IDs to a row that never
                        // had anything to do with them.
                        device_ids: newType === 'INV' ? undefined : newOrder.device_ids,
                        status_reason: (newType === 'BROKEN' || newType === 'LOST')
                          ? newOrder.status_reason
                          : undefined
                      });
                    }}
                    required
                  >
                    <option value="BROKEN">BROKEN</option>
                    <option value="IN">IN</option>
                    <option value="INV">INV</option>
                    <option value="LOST">LOST</option>
                    <option value="OUT">OUT</option>
                  </select>
                </div>

                {(newOrder.transaction_type === 'BROKEN' || newOrder.transaction_type === 'LOST') && (
                  <div className="form-group">
                    <label htmlFor="status-reason">Reason</label>
                    <select
                      id="status-reason"
                      value={newOrder.status_reason || ''}
                      onChange={(e) => setNewOrder({ ...newOrder, status_reason: e.target.value })}
                    >
                      <option value="">Select a reason…</option>
                      {statusReasons.map(r => (
                        <option key={r.id} value={r.reason}>{r.reason}</option>
                      ))}
                    </select>
                    <button type="button" className="link-button"
                            onClick={() => setAddingReason(true)}>+ Add reason</button>
                    {addingReason && (
                      <div className="add-reason-row">
                        <input type="text" value={newReason} placeholder="New reason"
                               onChange={(e) => setNewReason(e.target.value)} />
                        <button type="button" onClick={submitNewReason}>Save</button>
                      </div>
                    )}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="date">Order Date</label>
                  <input
                    id="date"
                    type="date"
                    className="date-input"
                    value={newOrder.order_date}
                    onChange={(e) => setNewOrder({
                      ...newOrder,
                      order_date: e.target.value
                    })}
                    required
                  />
                </div>

                {newOrder.transaction_type !== 'INV' && (
                  <div className="form-group">
                    <label htmlFor="customer">Customer</label>
                    <CustomerSelect
                      id="customer"
                      value={newOrder.customer_tag || ''}
                      onChange={(val) => setNewOrder({
                        ...newOrder,
                        customer_tag: val || undefined
                      })}
                      options={[...customers]
                        .sort((a, b) => displayName(a).localeCompare(displayName(b)))
                        .map(c => ({ value: c.customer_tag, label: displayName(c) }))}
                      placeholder="Search customers..."
                      required
                    />
                  </div>
                )}

                <div className="form-group submit-group">
                  <Button type="submit" className="submit-button"
                          disabled={packageLines.length === 0 && !newOrder.sku_code}>
                    {packageLines.length > 0
                      ? `Add ${packageLines.length} Lines`
                      : 'Add Order'}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        )}

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Product Name</th>
                <th>SKU Code</th>
                <th>Quantity</th>
                <th>Type</th>
                <th>Reason</th>
                <th>Devices</th>
                <th>Order Date</th>
                <th>Customer</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>
                  <td>{productNameFor(order)}</td>
                  <td>{order.sku_code}</td>
                  <td>
                    {editingOrderId === order.id ? (
                      <input
                        type="number"
                        min="1"
                        value={editingOrder.quantity}
                        onChange={(e) => 
                          setEditingOrder({
                            ...editingOrder,
                            quantity: parseInt(e.target.value) || 0
                          })
                        }
                      />
                    ) : (
                      order.quantity
                    )}
                  </td>
                  <td className={`transaction-type ${order.transaction_type.toLowerCase()}`}>
                    {editingOrderId === order.id ? (
                      <select
                        value={editingOrder.transaction_type}
                        onChange={(e) => 
                          setEditingOrder({
                            ...editingOrder,
                            transaction_type: e.target.value as 'IN' | 'OUT' | 'INV' | 'BROKEN' | 'LOST'
                          })
                        }
                      >
                        <option value="IN">IN</option>
                        <option value="OUT">OUT</option>
                        <option value="INV">INV</option>
                        <option value="BROKEN">BROKEN</option>
                        <option value="LOST">LOST</option>
                      </select>
                    ) : (
                      order.transaction_type
                    )}
                  </td>
                  <td>
                    {order.status_reason
                      ? <span className="status-reason">{order.status_reason}</span>
                      : <span className="cell-empty">—</span>}
                  </td>
                  <td>
                    {order.device_ids && order.device_ids.length > 0
                      ? <span className="device-list" title={order.device_ids.join(', ')}>
                          {order.device_ids.join(', ')}
                        </span>
                      : <span className="cell-empty">—</span>}
                  </td>
                  <td>{order.order_date ? formatDate(order.order_date) : 'N/A'}</td>
                  <td>
                    {editingOrderId === order.id ? (
                      <CustomerSelect
                        value={editingOrder.customer_tag || ''}
                        onChange={(val) => setEditingOrder({
                          ...editingOrder,
                          customer_tag: val || undefined
                        })}
                        options={[...customers]
                          .sort((a, b) => displayName(a).localeCompare(displayName(b)))
                          .map(c => ({ value: c.customer_tag, label: displayName(c) }))}
                        placeholder="Search..."
                        allowEmpty
                        emptyLabel="None"
                      />
                    ) : (
                      <span className={`customer-tag ${order.customer_tag ? '' : 'empty'}`}>
                        {customerLabel(order.customer_tag)}
                      </span>
                    )}
                  </td>
                  <td>
                    {editingOrderId === order.id ? (
                      <div className="action-buttons">
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => saveOrderChanges(order.id)}
                          aria-label="Save changes"
                        >
                          Save
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={cancelEditingOrder}
                          aria-label="Cancel editing"
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <div className="action-buttons">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="edit-button"
                          onClick={() => startEditingOrder(order)}
                          aria-label="Edit order"
                        >
                          ✎
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="delete-button"
                          onClick={() => deleteOrder(order.id)}
                          aria-label="Delete order"
                        >
                          ×
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr><td colSpan={7} className="empty-state">No orders found</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {orderTotal > 0 && (
          <div className="pagination">
            <span>Showing {orders.length} of {orderTotal} orders</span>
            {hasMoreOrders && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fetchOrders({ offset: orders.length, append: true })}
                disabled={loadingMoreOrders}
              >
                {loadingMoreOrders ? 'Loading…' : 'Load more'}
              </Button>
            )}
          </div>
        )}
      </div>

      {packageDraft.items.length > 0 && (
        <div className="package-modal-overlay" onClick={closePackageModal}>
          <div className="package-modal" onClick={(e) => e.stopPropagation()}>
            <div className="package-modal-header">
              <h3>{editingPackage ? `Edit ${editingPackage.name}` : 'New Package'}</h3>
              <button className="package-modal-close" onClick={closePackageModal} aria-label="Close">×</button>
            </div>
            {packageError && <Alert type="error">{packageError}</Alert>}
            <div className="package-form-field">
              <label htmlFor="package-name">Name</label>
              <input
                id="package-name"
                type="text"
                value={packageDraft.name}
                onChange={(e) => setPackageDraft(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. C8 Set"
              />
            </div>
            <div className="package-form-field">
              <label htmlFor="package-desc">Description</label>
              <input
                id="package-desc"
                type="text"
                value={packageDraft.description}
                onChange={(e) => setPackageDraft(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional"
              />
            </div>
            <div className="package-form-field">
              <label>Items</label>
              <div className="package-items-list">
                {packageDraft.items.map((item, idx) => (
                  <div key={idx} className="package-item-row">
                    <select
                      value={item.sku_code}
                      onChange={(e) => updatePackageItem(idx, { sku_code: e.target.value })}
                    >
                      <option value="">Select a SKU…</option>
                      {[...skus]
                        .sort((a, b) => a.product_name.localeCompare(b.product_name))
                        .map(sku => (
                          <option key={sku.sku_code} value={sku.sku_code}>
                            {sku.product_name}
                          </option>
                        ))}
                    </select>
                    <input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => updatePackageItem(idx, { quantity: Math.max(1, parseInt(e.target.value || '1', 10)) })}
                    />
                    <Button variant="ghost" size="sm" onClick={() => removePackageItem(idx)} aria-label="Remove item">×</Button>
                  </div>
                ))}
                <Button variant="ghost" size="sm" onClick={addPackageItem}>+ Add Item</Button>
              </div>
            </div>
            <div className="package-modal-actions">
              <Button variant="ghost" size="sm" onClick={closePackageModal} disabled={packageSaving}>Cancel</Button>
              <Button size="sm" onClick={savePackage} disabled={packageSaving}>
                {packageSaving ? 'Saving…' : editingPackage ? 'Save changes' : 'Create package'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryPage; 